import React, { useContext } from 'react';

import classnames from 'classnames';

import AutopilotSourceLogo from 'common/common/AutopilotSourceLogo';
import { SyncIntegrationNames } from 'common/constants/autopilotIntegrations';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import LazyLoadedImage from 'common/LazyLoadedImage';
import { Span } from 'common/ui/Text';
import styles from 'css-module/components/subdomain/admin/AdminQueue/_GenericSourceCell.module.scss';

import CannyLogo from 'img/canny-icon.svg';

type DraftSourceProps = {
  sourceType: 'draft';
  link: string | null;
  source: SyncIntegrationNames;
  hasDuplicatePost: boolean;
};

type PostSourceProps = {
  sourceType: 'post';
  urlName: string;
  hasDuplicatePost: boolean;
};

type Props = DraftSourceProps | PostSourceProps;

const SourceLabelMap: Record<SyncIntegrationNames, string> = {
  [SyncIntegrationNames.api]: 'API',
  [SyncIntegrationNames.intercom]: 'Intercom',
  [SyncIntegrationNames.helpscout]: 'Help Scout',
  [SyncIntegrationNames.zendesk]: 'Zendesk',
  [SyncIntegrationNames.gong]: 'Gong',
  [SyncIntegrationNames.zoom]: 'Zoom',
  [SyncIntegrationNames.trustpilotReviews]: 'Trustpilot',
  [SyncIntegrationNames.appStoreReviews]: 'App Store',
  [SyncIntegrationNames.freshdesk]: 'Freshdesk',
  [SyncIntegrationNames.playStoreReviews]: 'Google Play',
  [SyncIntegrationNames.g2Reviews]: 'G2',
  [SyncIntegrationNames.capterraReviews]: 'Capterra',
  [SyncIntegrationNames.trustradiusReviews]: 'TrustRadius',
  [SyncIntegrationNames.productHuntReviews]: 'Product Hunt',
  [SyncIntegrationNames.salesforceReviews]: 'Salesforce',
  [SyncIntegrationNames.wordpressReviews]: 'WordPress',
  [SyncIntegrationNames.shopifyReviews]: 'Shopify',
  [SyncIntegrationNames.tldv]: 'tl;dv',
  [SyncIntegrationNames.zapier]: 'Zapier',
};

const DefaultLinkMap: Record<SyncIntegrationNames, string | undefined> = {
  [SyncIntegrationNames.api]: undefined,
  [SyncIntegrationNames.intercom]: '/admin/settings/intercom',
  [SyncIntegrationNames.helpscout]: '/admin/settings/helpscout',
  [SyncIntegrationNames.zendesk]: '/admin/settings/zendesk',
  [SyncIntegrationNames.gong]: '/admin/settings/gong',
  [SyncIntegrationNames.freshdesk]: '/admin/settings/freshdesk',
  [SyncIntegrationNames.zoom]: '/admin/settings/zoom',
  [SyncIntegrationNames.trustpilotReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.appStoreReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.playStoreReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.g2Reviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.capterraReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.trustradiusReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.productHuntReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.salesforceReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.wordpressReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.shopifyReviews]: '/admin/settings/autopilot/feedback-discovery',
  [SyncIntegrationNames.tldv]: '/admin/settings/tldv',
  [SyncIntegrationNames.zapier]: '/admin/settings/autopilot/feedback-discovery',
};

const GenericSourceCell = (props: Props) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);

  if (props.sourceType === 'draft') {
    const { source, hasDuplicatePost, link } = props;

    const draftLink = link ?? DefaultLinkMap[source];
    const ariaLabel = draftLink
      ? `See more about where this ${SourceLabelMap[source]} post draft came from`
      : undefined;

    return (
      <a
        aria-label={ariaLabel}
        href={draftLink}
        target="_blank"
        className={styles.sourceLink}
        rel="noopener noreferrer nofollow">
        <AutopilotSourceLogo
          className={classnames(styles.queueSourceLogo, { duplicate: hasDuplicatePost })}
          integrationName={source}
          size="medium"
        />
        <Span className={styles.queueSourceLabel}>{SourceLabelMap[source]}</Span>
      </a>
    );
  }

  // Post source
  const { urlName, hasDuplicatePost } = props;
  return (
    <button
      className={styles.cannyPostButton}
      onClick={() =>
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: urlName },
        })
      }>
      <LazyLoadedImage
        className={classnames(styles.queueSourceLogo, { duplicate: hasDuplicatePost })}
        alt="canny"
        src={CannyLogo}
      />
      <Span className={styles.queueSourceLabel}>Canny</Span>
    </button>
  );
};

export default GenericSourceCell;
